import { TreeView } from '@fluid-experimental/tree';

import { getIndexedNodes, ITEMS, NodeKind } from '..';
import { isCollectionNode, isItemNode } from '../typeGuards';

import { getNodes, hasNone } from './deltaUtility';
import { DeltaNodeGroup, Match, MfsChange } from './types';
import { logVerbose } from '../../../log';

/** Check whether the delta matches an itemsChanged event. */
export function matchItemsChanged(view: TreeView, previousView: TreeView, delta: DeltaNodeGroup): Match<MfsChange[]> {
  const hasAdd = !hasNone(delta, 'added');
  const hasRemove = !hasNone(delta, 'removed');

  if (hasAdd && !hasRemove) {
    logVerbose('matchItemsChanged', 'match add hasItemsChanged');
    return hasItemsChange(delta, 'add');
  }

  if (hasRemove && !hasAdd) {
    logVerbose('matchItemsChanged', 'match add hasItemsChanged');
    return hasItemsChange(delta, 'remove');
  }

  logVerbose('matchCollectionChanged', 'match items failed');
  return Match.fail();
}

function hasItemsChange(delta: DeltaNodeGroup, changeKind: 'add' | 'remove'): Match<MfsChange[]> {
  const itemNode = getNodes(delta, changeKind, NodeKind.Item)[0];
  logVerbose('hasItemsChange', 'node identifier', { nodeIdentifier: itemNode.identifier, changeKind });
  // raise collection-specific event for mfs collection items.
  if (isCollectionNode(itemNode)) {
    logVerbose('hasItemsChange', 'collection match', { collectionId: itemNode.payload.id });
    return Match.ok([
      {
        collectionId: itemNode.payload.id,
        label: itemNode.payload.label,
        kind: 'collectionChanged',
        changeKind,
      },
    ]);
  }

  if (isItemNode(itemNode)) {
    logVerbose('hasItemsChange', 'collection match', { itemId: itemNode.payload.id });
    return Match.ok([
      {
        itemId: itemNode.payload.id,
        label: itemNode.payload.label,
        kind: 'itemsChanged',
        changeKind,
      },
    ]);
  }

  logVerbose('hasItemsChange', 'match items failed', { nodeIdentifier: itemNode.identifier, changeKind });

  return Match.fail();
}

/** Checks whether the given change is a clearing of root items. */
export function matchItemsCleared(view: TreeView, previousView: TreeView): Match<MfsChange[]> {
  // if clear was called when single item existed, we'll just raise an itemsChanged/removed event that should be fine.
  const viewItemCount = getItemCount(view);
  const previousViewItemCount = getItemCount(previousView);
  const isClearing = viewItemCount === 0 && previousViewItemCount != 1;
  logVerbose('matchItemsCleared', 'check if change is clearing of root items', {
    viewItemCount,
    previousViewItemCount,
    isClearing,
  });
  return isClearing ? Match.ok([{ kind: 'itemsChanged', changeKind: 'clear' }]) : Match.fail();
}

function getItemCount(view: TreeView): number {
  const itemNodes = getIndexedNodes(view, ITEMS);
  return itemNodes.length;
}
